import { Megaphone01Icon } from "@hugeicons-pro/core-bulk-rounded";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { MetricTiles } from "@/components/dashboard/metric-tiles";
import { ChartCard } from "@/components/dashboard/chart-card";
import { TrendArea, CategoryBars } from "@/components/dashboard/charts";
import { BreakdownTable, type Column } from "@/components/dashboard/breakdown-table";
import { platformLabelCol, exactReachCol, exactErCol, impressionsCol } from "@/components/dashboard/columns";
import { AwarenessDefinitions } from "@/components/dashboard/awareness-definitions";
import { Skeleton } from "@/components/ui/skeleton";
import { FadeIn } from "@/components/ui/motion";
import { seriesColor } from "@/lib/chart-theme";
import { useTotals, useSeries, useBreakdown, useCollaborations } from "@/lib/use-metrics";
import { useExactReach, reachCaveats, type ReachValue } from "@/lib/use-reach";
import { useReference, channelRows } from "@/lib/use-reference";
import { collabStats } from "@/lib/api/collabs";
import { compact } from "@/lib/format";
import type { BreakdownRow } from "@/lib/api/metrics";

const followers = (r: BreakdownRow) => (r.followers_total ? compact(r.followers_total) : "—");
const growth = (r: BreakdownRow) => (r.follower_growth ? `${r.follower_growth > 0 ? "+" : ""}${compact(r.follower_growth)}` : "—");

/** A tile compares with the previous period only when both periods cover the
 *  same parts exactly — otherwise the "change" would be a change in coverage. */
const comparable = (cur: ReachValue | undefined, prev: ReachValue | undefined) => !!cur?.exact && !!prev?.exact;

export function Awareness() {
  const { metrics, platforms } = useReference();
  const totals = useTotals("awareness");
  const series = useSeries("awareness");
  const byPlatform = useBreakdown("platform", "awareness");
  const byBrand = useBreakdown("brand", "awareness");
  const collabs = useCollaborations();
  const reach = useExactReach({ activity: "awareness" });
  const reachPrev = useExactReach({ activity: "awareness", previous: true });

  const loading = totals.isPending;
  const activeCollabs = collabs.data ? collabStats(collabs.data).active : null;
  const total = reach.get();
  const totalPrev = reachPrev.get();
  const kpis = totals.data?.kpis;
  const values = {
    ...(kpis ?? {}),
    reached_accounts: total?.reach ?? null,
    organic_engagement_rate: total?.er ?? null,
    // 0 means no follower snapshot inside the window, not zero followers.
    followers_total: kpis?.followers_total || null,
    influencer_collabs: activeCollabs,
  };
  const prev = {
    ...((totals.data?.prev ?? {}) as unknown as Record<string, number | null>),
    reached_accounts: comparable(total, totalPrev) ? totalPrev!.reach : null,
    // ER rests on impressions, not reach, so every window compares.
    organic_engagement_rate: totalPrev?.er ?? null,
    followers_total: totals.data?.prev.followers_total || null,
  };
  const caveat = reachCaveats(total).join(" ") || undefined;
  const platformRows = channelRows(byPlatform.data ?? [], platforms);

  const platformColumns: Column<BreakdownRow>[] = [
    platformLabelCol("Platform", platforms),
    exactReachCol(reach, "platform"),
    impressionsCol(),
    { key: "engagements", header: "Engagement", align: "right", render: (r) => (r.engagements ? compact(r.engagements) : "—"), exportValue: (r) => r.engagements ?? "" },
    exactErCol(reach, "platform"),
    { key: "views", header: "Video views", align: "right", render: (r) => (r.video_views ? compact(r.video_views) : "—"), exportValue: (r) => r.video_views ?? "" },
    { key: "followers_total", header: "Volgers", align: "right", render: followers, exportValue: (r) => r.followers_total || "" },
    { key: "followers", header: "Groei", align: "right", render: growth, exportValue: (r) => r.follower_growth ?? "" },
    { key: "content", header: "Content", align: "right", render: (r) => (r.content_pieces ? compact(r.content_pieces) : "—"), exportValue: (r) => r.content_pieces ?? "" },
  ];

  // Brands without reach in the window (Atelier Luxus and Maison Vervloet only
  // have website visitors today) are left out rather than drawn as empty bars.
  const brandBars = (byBrand.data ?? [])
    .map((r) => ({ label: r.label, reach: reach.get("brand", r.key)?.reach ?? 0 }))
    .filter((r) => r.reach > 0);
  const brandColumns: Column<BreakdownRow>[] = [
    { key: "label", header: "Merk", render: (r) => <span className="font-medium">{r.label}</span>, exportValue: (r) => r.label },
    exactReachCol(reach, "brand"),
    { key: "engagements", header: "Engagement", align: "right", render: (r) => (r.engagements ? compact(r.engagements) : "—"), exportValue: (r) => r.engagements ?? "" },
    exactErCol(reach, "brand"),
    { key: "followers_total", header: "Volgers", align: "right", render: followers, exportValue: (r) => r.followers_total || "" },
    { key: "followers", header: "Groei", align: "right", render: growth, exportValue: (r) => r.follower_growth ?? "" },
    { key: "content", header: "Content", align: "right", render: (r) => (r.content_pieces ? compact(r.content_pieces) : "—"), exportValue: (r) => r.content_pieces ?? "" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Awareness"
        icon={Megaphone01Icon}
        title="Merkbekendheid & organisch"
        description="Bereik zoals elk platform het zelf voor de gekozen periode telt, engagement, video views en Instagram-volgers."
        tabs={<GlobalFilterBar showActivity={false} activity="awareness" />}
      />

      {loading ? (
        <Skeleton className="h-44 rounded-2xl" />
      ) : (
        <MetricTiles
          keys={["reached_accounts", "organic_engagement_rate", "followers_total", "follower_growth", "video_views", "content_pieces", "website_visitors", "influencer_collabs"]}
          values={values}
          prev={prev}
          metrics={metrics}
          labels={{ follower_growth: "Groei" }}
          notes={{ reached_accounts: caveat }}
          pending={reach.loading ? ["reached_accounts", "organic_engagement_rate"] : []}
        />
      )}

      <FadeIn delay={0.05}>
        <div className="grid gap-4 lg:grid-cols-2">
          <ChartCard title="Bereik & engagement" subtitle="Per dag · dagbereik van elk platform opgeteld" bodyClassName="h-80">
            <TrendArea
              data={series.data ?? []}
              series={[
                { dataKey: "reach", name: "Dagbereik", color: seriesColor("reach"), axis: "left" },
                { dataKey: "engagements", name: "Engagement", color: seriesColor("engagements"), axis: "right" },
              ]}
            />
          </ChartCard>
          <ChartCard title="Bereik per merk" subtitle="Periodebereik per platform, opgeteld per merk" bodyClassName="h-80">
            <CategoryBars data={brandBars} dataKey="reach" />
          </ChartCard>
        </div>
      </FadeIn>

      <FadeIn delay={0.08}>
        <BreakdownTable
          title="Per merk"
          description="Bereik per platform voor de hele periode (opgeteld over de platformen van het merk), engagement, Instagram-volgers aan het einde van de periode en netto groei."
          rows={byBrand.data ?? []}
          columns={brandColumns}
          rowKey={(r) => r.key}
          exportName="Prado Pulse — Awareness per merk"
        />
      </FadeIn>

      <FadeIn delay={0.1}>
        <BreakdownTable
          title="Per channel"
          description="Elk cijfer zoals het platform het zelf rapporteert voor de gekozen periode. ER = engagement gedeeld door bereik."
          rows={platformRows}
          columns={platformColumns}
          rowKey={(r) => r.key}
          exportName="Prado Pulse — Awareness per channel"
        />
      </FadeIn>

      <FadeIn delay={0.12}>
        <AwarenessDefinitions />
      </FadeIn>
    </div>
  );
}
